import {
  WSActions,
  WSControlGroup,
  WSElement,
  WSForm,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  EmailVerificationStatus,
  OtpVerificationChannel,
  PhoneVerificationStatus
} from "@wingspanhq/users/dist/lib/interfaces";
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useWSMutation } from "../../query/helpers";
import { useUserId } from "../../query/hooks/helpers";
import { useQueryUserProfile } from "../../query/users/queries";
import { usersService } from "../../services/users";
import { WSServiceError } from "../../utils/serviceHelper";
import { censorEmail, censorPhone } from "../../utils/stringHelper";
import { ErrorAlert } from "@components/ErrorAlert";

type FormData = { channel: OtpVerificationChannel };

type Props = {
  callbackUrl?: string;
  onSuccess: (data: FormData) => void;
  preferPhone?: boolean;
};

export const OtpRequestForm: React.FC<Props> = props => {
  const location = useLocation();
  const userId = useUserId();
  const userQuery = useQueryUserProfile(userId);
  const [submit, submitMeta] = useWSMutation<
    FormData,
    WSServiceError,
    FormData
  >(
    async data => {
      await usersService.session.beginOtpSession({
        channel: data.channel,
        appRedirectPath:
          props.callbackUrl || `${location.pathname}${location.search}`
      });

      return data;
    },
    {
      onSuccess: props.onSuccess
    }
  );

  const user = userQuery.data;

  const channelOptions = useMemo(() => {
    if (!user) return [];

    const options = [];

    const isEmailVerified =
      user.emailVerificationState?.status === EmailVerificationStatus.Verified;
    const isPhoneVerified =
      user.phone.status === PhoneVerificationStatus.Verified;

    if (props.preferPhone) {
      if (isPhoneVerified) {
        options.push({
          value: OtpVerificationChannel.SMS,
          label: `Phone (${censorPhone(user.phone.number as string)})`
        });
      } else {
        if (isEmailVerified) {
          options.push({
            value: OtpVerificationChannel.Email,
            label: `Email (${censorEmail(user.email)})`
          });
        }
      }
    } else {
      if (isEmailVerified) {
        options.push({
          value: OtpVerificationChannel.Email,
          label: `Email (${censorEmail(user.email)})`
        });
      }

      if (isPhoneVerified) {
        options.push({
          value: OtpVerificationChannel.SMS,
          label: `Phone (${censorPhone(user.phone.number as string)})`
        });
      }
    }

    return options;
  }, [props.preferPhone, user]);

  const channelOptionsText = useMemo(() => {
    return channelOptions
      .map((option, index) => {
        let text = "";

        switch (option.value) {
          case OtpVerificationChannel.SMS:
            text =
              "a 6 digit verification code will be sent via SMS to your mobile device (Message and Data rates may apply)";
            break;
          case OtpVerificationChannel.Email:
            text = "a verification link will be sent to your email address";
            break;
        }

        if (index === 0) {
          text = text[0].toUpperCase() + text.slice(1);
        }

        return text;
      })
      .join(" or ");
  }, [channelOptions]);

  if (channelOptions.length === 0) {
    return (
      <WSText color="gray600">
        Two-factor authentication is required for your account, but no phone
        number has been set up. To continue, please add a phone number in your
        account settings.
      </WSText>
    );
  }

  return (
    <WSElement>
      <WSForm<FormData>
        onSubmit={submit}
        defaultValues={{
          channel: channelOptions[0].value
        }}
      >
        <WSText color="gray600" mb="XL" align="left">
          {channelOptionsText}.
        </WSText>

        <WSForm.Field
          mb="2XL"
          name="channel"
          component={WSControlGroup}
          componentProps={{
            options: channelOptions,
            type: "radio"
          }}
          label="Select"
        />

        <ErrorAlert mt="M" error={submitMeta.error} context="VerifyPhone" />
        <WSActions
          buttons={[
            {
              label: "Continue",
              loading: submitMeta.isLoading,
              type: "submit",
              fullWidth: true
            }
          ]}
        />
      </WSForm>
    </WSElement>
  );
};
